import React, { useState } from 'react';
import { 
  Users, 
  User,
  Wrench,
  MapPin,
  Clock,
  Phone,
  Calendar,
  Star,
  CheckCircle,
  AlertTriangle,
  Plus,
  Edit,
  Eye,
  Navigation,
  Package,
  Award
} from 'lucide-react';
import Card from '../../components/Card';

const InstallationTeams = () => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Echipele de instalare TIBECO
  const installationTeams = [
    {
      id: 'TEAM-001',
      name: 'Echipa Ștefan',
      leader: 'Ștefan Georgescu',
      leaderId: 'EMP-008',
      status: 'active',
      rating: 4.8,
      specialization: 'Ferestre PVC & Aluminiu',
      experience: '8+ ani',
      members: [
        {
          id: 'EMP-008',
          name: 'Ștefan Georgescu',
          role: 'Șef Echipă',
          phone: '+40722345678',
          skills: ['PVC', 'Aluminiu', 'Management echipă'],
          rating: 4.9,
          installations: 156
        },
        {
          id: 'EMP-011',
          name: 'Beniamin Stoica',
          role: 'Montator Senior',
          phone: '+40734567890',
          skills: ['PVC', 'Feronerie', 'Reparații'],
          rating: 4.7,
          installations: 89
        }
      ],
      currentProjects: [
        {
          id: 'INST-2024-045',
          client: 'Familia Popescu',
          address: 'Str. Florilor 25, Sector 2',
          type: 'Ferestre PVC',
          status: 'in_progress',
          progress: 60,
          startDate: '2025-01-06',
          estimatedFinish: '2025-01-08'
        },
        {
          id: 'INST-2024-046',
          client: 'Vila Baneasa',
          address: 'Șos. București-Nord 15',
          type: 'Ferestre Aluminiu',
          status: 'scheduled',
          progress: 0,
          startDate: '2025-01-10',
          estimatedFinish: '2025-01-12'
        }
      ],
      equipment: ['Bormasină profesională', 'Nivela laser', 'Kit etanșare', 'Scule montaj'],
      performance: {
        completedJobs: 45,
        averageTime: '2.3 zile/proiect',
        customerRating: 4.8,
        issuesRate: 2.1
      }
    },
    {
      id: 'TEAM-002', 
      name: 'Echipa Eusebiu',
      leader: 'Eusebiu Marinescu',
      leaderId: 'EMP-009',
      status: 'active',
      rating: 4.6,
      specialization: 'Uși de Intrare & Security',
      experience: '6+ ani',
      members: [
        {
          id: 'EMP-009',
          name: 'Eusebiu Marinescu',
          role: 'Specialist Uși',
          phone: '+40745678901',
          skills: ['Uși blindate', 'Sisteme securitate', 'Încuietori'],
          rating: 4.8,
          installations: 134
        }
      ],
      currentProjects: [
        {
          id: 'INST-2024-047',
          client: 'Hotel Continental',
          address: 'Calea Victoriei 78',
          type: 'Uși de Intrare',
          status: 'in_progress',
          progress: 75,
          startDate: '2025-01-05',
          estimatedFinish: '2025-01-09'
        }
      ],
      equipment: ['Mașină de frezat', 'Kit instalare încuietori', 'Nivela cu bule', 'Burghie speciale'],
      performance: {
        completedJobs: 38,
        averageTime: '1.8 zile/proiect',
        customerRating: 4.6,
        issuesRate: 1.8
      }
    },
    {
      id: 'TEAM-003',
      name: 'Echipa Mixt',
      leader: 'Rotativ',
      leaderId: null,
      status: 'standby',
      rating: 4.4,
      specialization: 'Proiecte Speciale & Urgențe',
      experience: 'Variabil',
      members: [
        {
          id: 'FLEX-001',
          name: 'Membrii disponibili',
          role: 'Rotativ',
          phone: 'Variabil',
          skills: ['Flexibilitate', 'Urgențe', 'Support'],
          rating: 4.4,
          installations: 'Variabil'
        }
      ],
      currentProjects: [],
      equipment: ['Echipament mobil', 'Unelte urgență', 'Kit reparații rapide'],
      performance: {
        completedJobs: 12,
        averageTime: '3.1 zile/proiect',
        customerRating: 4.4,
        issuesRate: 3.2
      }
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'busy': return 'bg-red-100 text-red-800 border-red-200';
      case 'standby': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Activ';
      case 'busy': return 'Ocupat';  
      case 'standby': return 'Standby';
      default: return 'Necunoscut';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-600';
    if (progress >= 50) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  // Statistici generale
  const totalTeams = installationTeams.length;
  const activeTeams = installationTeams.filter(t => t.status === 'active').length;
  const totalProjects = installationTeams.reduce((sum, team) => sum + team.currentProjects.length, 0);
  const avgRating = (installationTeams.reduce((sum, team) => sum + team.rating, 0) / totalTeams).toFixed(1);

  const TeamModal = ({ team, onClose }) => {
    if (!team) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl m-4 max-h-screen overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-primary-600" />
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {team.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {team.specialization} • {team.experience}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
            {['overview', 'members', 'projects', 'performance'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {tab === 'overview' && 'Overview'}
                {tab === 'members' && 'Membrii Echipei'}
                {tab === 'projects' && 'Proiecte Active'}
                {tab === 'performance' && 'Performanță'}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Informații Generale
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Lider echipă:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {team.leader}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(team.status)}`}>
                          {getStatusText(team.status)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Rating:</span>
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-400 mr-1" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {team.rating}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Membri:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {team.members.length}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Echipament
                    </h4>
                    <div className="space-y-2">
                      {team.equipment.map((item, index) => (
                        <div key={index} className="flex items-center text-sm">
                          <Package className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-600 dark:text-gray-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Performanță Rapidă
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600">
                          {team.performance.completedJobs}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          Proiecte Complete
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {team.performance.customerRating}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          Rating Clienți
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="space-y-4">
                {team.members.map((member, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">
                            {member.name}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {member.role}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-400" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {member.rating}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-300">Telefon:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{member.phone}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-300">Instalări:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{member.installations}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-300">Skills:</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {Array.isArray(member.skills) ? member.skills.join(', ') : member.skills}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'projects' && (
              <div className="space-y-4">
                {team.currentProjects.length > 0 ? team.currentProjects.map((project, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white">
                          {project.client}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {project.address}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project.status)}`}>
                        {project.status === 'in_progress' ? 'În Progres' : 'Programat'}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300">Progres:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {project.progress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project.progress)}`}
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-gray-600 dark:text-gray-300">Început:</span>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {new Date(project.startDate).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-300">Finalizare:</span>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {new Date(project.estimatedFinish).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">
                      Nu există proiecte active în acest moment
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'performance' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                    Metrici Performanță
                  </h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Proiecte Complete:</span>
                      <span className="font-bold text-primary-600">
                        {team.performance.completedJobs}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Timp Mediu:</span>
                      <span className="font-bold text-green-600">
                        {team.performance.averageTime}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Rating Clienți:</span>
                      <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-400 mr-1" />
                        <span className="font-bold text-yellow-600">
                          {team.performance.customerRating}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Rata Probleme:</span>
                      <span className="font-bold text-red-600">
                        {team.performance.issuesRate}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                    Evaluare Generală
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Calitate</span>
                        <span className="text-sm font-medium">92%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: '92%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Promptitudine</span>
                        <span className="text-sm font-medium">88%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '88%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Comunicare</span>
                        <span className="text-sm font-medium">95%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: '95%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Închide
            </button>
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              Editează Echipă
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Users className="w-8 h-8 text-secondary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Echipe Instalare
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Management echipe montaj și instalare TIBECO
            </p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors">
          <Plus className="w-4 h-4" />
          Echipă Nouă
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalTeams}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Echipe Totale
              </div>
            </div>
            <Users className="w-8 h-8 text-secondary-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {activeTeams}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Echipe Active
              </div>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {totalProjects}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Proiecte Active
              </div>
            </div>
            <Wrench className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-yellow-600">
                {avgRating}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                Rating Mediu
              </div>
            </div>
            <Star className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {installationTeams.map((team) => (
          <Card key={team.id} className="hover:shadow-lg transition-shadow">
            <div className="p-6">
              {/* Team Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary-400 to-primary-400 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {team.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {team.leader}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(team.status)}`}>
                  {getStatusText(team.status)}
                </span>
              </div>

              {/* Team Info */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Specializare:</span>
                  <span className="font-medium text-gray-900 dark:text-white text-right">
                    {team.specialization}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Membri:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {team.members.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Proiecte active:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {team.currentProjects.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Rating:</span>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-yellow-400 mr-1" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      {team.rating}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setSelectedTeam(team);
                    setShowModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Detalii
                </button>
                <button className="flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                  <Navigation className="w-4 h-4" />
                  Urmărește
                </button>
                <button className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Team Modal */}
      {showModal && (
        <TeamModal
          team={selectedTeam}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default InstallationTeams;